import { useEffect, useState } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import OTPInput from '../components/CodeInput';
import { OtpButton } from '../components/Button';
import Cookies from 'js-cookie';
import { useOtpForm, useVerifyOtpMutation } from '../hooks';
import { resendOtp } from '../api';
import { OtpForm } from '../hooks';
import { toast } from 'react-toastify';

const ControlledOtpInput = () => {
  const { setValue } = useFormContext<OtpForm>();

  const handleOtpChange = (value: string) => {
    setValue('otp', value);
  };

  return <OTPInput length={6} onChange={handleOtpChange} />;
};

const RESEND_WAIT_TIME = 60; // seconds

export const Otp = () => {
  const form = useOtpForm();
  const token = Cookies.get('pre_auth_token');
  const { handleSubmit } = form;

  const { mutate, isPending } = useVerifyOtpMutation();
  const navigate = useNavigate();

  const onSubmit = (data: OtpForm) => {
    mutate(data);
  };

  const [countdown, setCountdown] = useState(0);
  const [resendEnabled, setResendEnabled] = useState(true);

  // On mount, restore countdown from localStorage
  useEffect(() => {
    const storedExpiry = localStorage.getItem('otpResendExpiresAt');
    const now = Date.now();

    if (storedExpiry) {
      const expiresAt = parseInt(storedExpiry, 10);
      const remaining = Math.ceil((expiresAt - now) / 1000);

      if (remaining > 0) {
        setCountdown(remaining);
        setResendEnabled(false);
        return;
      } else {
        localStorage.removeItem('otpResendExpiresAt');
      }
    }

    // No stored expiry: it's the first visit — disable the button and start countdown
    const newExpiresAt = now + RESEND_WAIT_TIME * 1000;
    localStorage.setItem('otpResendExpiresAt', newExpiresAt.toString());
    setCountdown(RESEND_WAIT_TIME);
    setResendEnabled(false);
  }, []);

  // Handle countdown timer
  useEffect(() => {
    if (countdown <= 0) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          localStorage.removeItem('otpResendExpiresAt');
          setResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  const handleResend = async () => {
    if (!resendEnabled) return;

    try {
      await resendOtp({ preAuthToken: token! });
      toast.success('OTP resent successfully');

      const expiresAt = Date.now() + RESEND_WAIT_TIME * 1000;
      localStorage.setItem('otpResendExpiresAt', expiresAt.toString());

      setCountdown(RESEND_WAIT_TIME);
      setResendEnabled(false);
    } catch (err: any) {
      toast.error('Session expired.');
      navigate('/');
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
    } else {
      form.setValue('preAuthToken', token, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, []);

  return (
    <FormProvider {...form}>
      <Stack
        component="form"
        width={'330px'}
        spacing={2}
        sx={{
          mt: 9,
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          align="center"
          gutterBottom
          sx={{ color: '#33408e' }}
        >
          Verify OTP
        </Typography>
        <Typography
          variant="body1"
          align="center"
          mb={3}
          sx={{ color: '#4c4c4c' }}
        >
          Enter the 4-digit login code sent to <br /> your email
        </Typography>

        <Box display="flex" justifyContent="center">
          <ControlledOtpInput />
        </Box>

        <Box display="flex" justifyContent="center">
          <OtpButton
            type="submit"
            fullWidth
            disabled={form.watch('otp').length < 6}
          >
            {isPending ? 'Verifying...' : 'Verify OTP'}
          </OtpButton>
        </Box>

        <Box mt={3} mb={3} textAlign="center">
          <Link
            underline="none"
            fontSize={13}
            sx={{
              color: resendEnabled ? '#33408e !important' : 'grey !important',
              cursor: resendEnabled ? 'pointer' : 'default',
              fontWeight: 600,
              pointerEvents: resendEnabled ? 'auto' : 'none',
            }}
            onClick={handleResend}
          >
            {resendEnabled ? 'Resend' : `Resend (${countdown})`}
          </Link>
        </Box>

        <Box mt={3} mb={3} textAlign="center">
          <Link
            underline="hover"
            fontSize={13}
            sx={{ color: '#4c4c4c !important' }}
            onClick={() => navigate('/')}
          >
            Back to Login
          </Link>
        </Box>
      </Stack>
    </FormProvider>
  );
};